import { useState, type ComponentProps, type ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { targetBrandConfig } from 'AppBranding';
import { type AdmissionOffer } from 'AdmissionOffer';
import { type Cohort } from 'Cohorts';
import { getIsExecEd, getRegistrationDeadline as getCohortRegistrationDeadline, getStartDate } from 'Cohorts';
import { DISCUSS_OTHER_OPTIONS, type CohortSectionOffer } from 'CohortSectionOffer';
import { useAngularContext } from 'AngularContext';
import { InlineLink } from 'InlineLink';
import { MailtoLink } from 'MailtoLink';
import { FormProvider, useForm, useWatch } from 'FrontRoyalReactHookForm';
import { getProgramConfigValue } from 'Program';
import { isPartialScholarship } from 'Scholarship';
import { acceptsWireTransfer } from 'TuitionPlan';
import { useRequiredCurrentUser } from 'FrontRoyalAngular';
import { type CurrentUser, getAdmissionOffer, getCohort } from 'Users';
import { LOCALE_PREFIX, stripeProductIdFor } from '../constants';
import { useCohortDates } from '../useCohortDates';
import { useRedirectHomeWithoutCohortSectionOffer } from '../useRedirectHomeWithoutCohortSectionOffer';
import { useCohortRegistrationOptions } from '../useCohortRegistrationOptions';
import { usePlanSelectionOptions } from '../usePlanSelectionOptions';
import { useStripeCheckoutSession } from '../useStripeCheckoutSession';
import { type FinancingOption, PaymentView, type PlanSelectionOptions } from '../TuitionAndRegistration.types';
import { BankDetailsModal } from './BankDetailsModal';
import { PaymentOptionsModal } from './PaymentOptionsModal';
import { PlanSelectionFooter, PlanSelectionIntro, showsRegistrationHeader } from './PlanSelectionSections';
import { RegistrationCard } from './RegistrationLayout';
import { SmallPrint, SmallPrintLine } from './SmallPrint';

type FormValues = {
    selectedPaymentOptionId: string;
    registerForCohortId: string;
};

export function CohortRegistrationPlanSelection(): ReactElement | null {
    const currentUser = useRequiredCurrentUser();

    const cohort = getCohort(currentUser);
    const admissionOffer = getAdmissionOffer(currentUser);
    const cohortSectionOffer = admissionOffer?.cohortSectionOffers?.[0];

    useRedirectHomeWithoutCohortSectionOffer({
        cohortSectionOffer,
        currentUser,
        screenName: 'cohort_registration_plan_selection',
    });

    if (!cohort || !admissionOffer || !cohortSectionOffer) return null;

    return (
        <PlanSelectionBody
            currentUser={currentUser}
            cohort={cohort}
            admissionOffer={admissionOffer}
            cohortSectionOffer={cohortSectionOffer}
        />
    );
}

type PlanSelectionBodyProps = {
    currentUser: CurrentUser;
    cohort: Cohort;
    admissionOffer: AdmissionOffer;
    cohortSectionOffer: CohortSectionOffer;
};

function PlanSelectionBody({
    currentUser,
    cohort,
    admissionOffer,
    cohortSectionOffer,
}: PlanSelectionBodyProps): ReactElement {
    const dates = useCohortDates(currentUser);
    const [showOptionsModal, setShowOptionsModal] = useState(false);
    const [showBankDetailsModal, setShowBankDetailsModal] = useState(false);

    const isTwoMonthExecEd = getIsTwoMonthExecEd(cohort);

    const { selectablePaymentSituations, additionalFinancingOptions, paymentOptions, discussOtherOptionsAvailable } =
        usePlanSelectionOptions(cohortSectionOffer);

    const registrationOptions = useCohortRegistrationOptions(currentUser, dates?.classStartDate ?? '');

    const methods = usePlanSelectionForm({
        defaultPaymentOptionId: paymentOptions[0]?.optionId ?? '',
        defaultCohortId: registrationOptions.defaultCohortId ?? admissionOffer.cohortId,
    });

    const { selectedPaymentOptionId, selectedFinancingOption, selectedPlanAcceptsWireTransfer } = useSelectedPlan({
        selectablePaymentSituations,
        additionalFinancingOptions,
        methods,
    });

    const {
        activePaymentView,
        goBackToPlanSelection,
        processingRegistration,
        registerRequiresRedirectToBrowser,
        handleRegister,
    } = useRegistrationFlow({
        admissionOffer,
        cohort,
        cohortSectionOffer,
        methods,
        selectedFinancingOption,
    });

    const onSelectPlanView = activePaymentView === PaymentView.select_plan;

    const isDisabled = !dates?.registerEnabled || processingRegistration;

    return (
        <FormProvider {...methods}>
            <RegistrationCard flushTop={!showsRegistrationHeader({ isTwoMonthExecEd, cohortSectionOffer })}>
                <PlanSelectionIntro
                    cohort={cohort}
                    admissionOffer={admissionOffer}
                    cohortSectionOffer={cohortSectionOffer}
                    dates={dates}
                    isTwoMonthExecEd={isTwoMonthExecEd}
                    registerRequiresRedirectToBrowser={registerRequiresRedirectToBrowser}
                />

                <PlanSelectionFooter
                    activePaymentView={activePaymentView}
                    paymentOptions={paymentOptions}
                    selectedPaymentOptionId={selectedPaymentOptionId}
                    selectedFinancingOption={selectedFinancingOption}
                    registrationOptions={registrationOptions}
                    isDisabled={isDisabled}
                    processingRegistration={processingRegistration}
                    registerRequiresRedirectToBrowser={registerRequiresRedirectToBrowser}
                    onExplainOptions={() => setShowOptionsModal(true)}
                    onBack={goBackToPlanSelection}
                    onRegister={handleRegister}
                />

                <PlanSelectionModals
                    currentUser={currentUser}
                    paymentOptions={paymentOptions}
                    singlePaymentSituation={selectablePaymentSituations.length === 1}
                    showOptionsModal={showOptionsModal}
                    onCloseOptionsModal={() => setShowOptionsModal(false)}
                    showBankDetailsModal={showBankDetailsModal}
                    onCloseBankDetailsModal={() => setShowBankDetailsModal(false)}
                />
            </RegistrationCard>

            <PlanSelectionSmallPrint
                currentUser={currentUser}
                cohort={cohort}
                cohortSectionOffer={cohortSectionOffer}
                showsRateAndApprovalNote={
                    onSelectPlanView &&
                    !!selectedFinancingOption &&
                    selectedFinancingOption.additionalFinancingOption.label !== DISCUSS_OTHER_OPTIONS
                }
                showsWireTransferNote={onSelectPlanView && selectedPlanAcceptsWireTransfer}
                fundingEmail={
                    onSelectPlanView && !discussOtherOptionsAvailable
                        ? getFundingEmail(currentUser, cohort, admissionOffer)
                        : null
                }
                onViewWireTransferInstructions={() => setShowBankDetailsModal(true)}
            />
        </FormProvider>
    );
}

// The two dialogs the screen can open: the plan explainer and the wire transfer bank details.
function PlanSelectionModals({
    currentUser,
    paymentOptions,
    singlePaymentSituation,
    showOptionsModal,
    onCloseOptionsModal,
    showBankDetailsModal,
    onCloseBankDetailsModal,
}: {
    currentUser: CurrentUser;
    paymentOptions: ComponentProps<typeof PaymentOptionsModal>['paymentOptions'];
    singlePaymentSituation: boolean;
    showOptionsModal: boolean;
    onCloseOptionsModal: () => void;
    showBankDetailsModal: boolean;
    onCloseBankDetailsModal: () => void;
}): ReactElement | null {
    if (showOptionsModal) {
        return <PaymentOptionsModal paymentOptions={paymentOptions} onClose={onCloseOptionsModal} />;
    }

    if (showBankDetailsModal) {
        return (
            <BankDetailsModal
                currentUser={currentUser}
                singlePaymentSituation={singlePaymentSituation}
                onClose={onCloseBankDetailsModal}
            />
        );
    }

    return null;
}

function usePlanSelectionForm({
    defaultPaymentOptionId,
    defaultCohortId,
}: {
    defaultPaymentOptionId: string;
    defaultCohortId: string;
}) {
    return useForm<FormValues>({
        defaultValues: {
            selectedPaymentOptionId: defaultPaymentOptionId,
            registerForCohortId: defaultCohortId,
        },
    });
}

type UseRegistrationFlowOptions = {
    admissionOffer: AdmissionOffer;
    cohort: Cohort;
    cohortSectionOffer: CohortSectionOffer;
    methods: ReturnType<typeof useForm<FormValues>>;
    selectedFinancingOption?: FinancingOption;
};

/*
    Owns which view the learner is on and what the register button does there. Ports the old
    `register` handler: third-party financing ends at its own message, and anything else opens
    Stripe Checkout, which collects the payment method.
*/
function useRegistrationFlow({
    admissionOffer,
    cohort,
    cohortSectionOffer,
    methods,
    selectedFinancingOption,
}: UseRegistrationFlowOptions) {
    const injector = useAngularContext();
    const [activePaymentView, setActivePaymentView] = useState(PaymentView.select_plan);
    const { processingRegistration, registerRequiresRedirectToBrowser, openStripeCheckoutSessionOrBrowser } =
        useStripeCheckoutSession();

    const handleRegister = () => {
        const values = methods.getValues();

        if (selectedFinancingOption) {
            setActivePaymentView(PaymentView.financing_option_selected);
            logFinancingOptionSelected(injector, {
                admissionOffer,
                cohort,
                cohortSectionOffer,
                registerForCohortId: values.registerForCohortId,
                option: selectedFinancingOption,
            });
            return;
        }

        openStripeCheckoutSessionOrBrowser({
            admission_offer_id: admissionOffer.id,
            cohort_id: values.registerForCohortId,
            product_id: stripeProductIdFor(admissionOffer),
            selected_tuition_plan_id: values.selectedPaymentOptionId,
        });
    };

    return {
        activePaymentView,
        goBackToPlanSelection: () => setActivePaymentView(PaymentView.select_plan),
        processingRegistration,
        registerRequiresRedirectToBrowser,
        handleRegister,
    };
}

// Two-month ExecEd from cycle 77 onward has its own stripped-down layout.
function getIsTwoMonthExecEd(cohort: Cohort): boolean {
    return (
        getProgramConfigValue<number>(cohort.programType, 'numMonths') === 2 &&
        getStartDate(cohort) >= new Date('2026/06/08')
    );
}

// FIXME: Remove the reportsTags check once the magic link is disabled and the offers are
// processed. See https://trello.com/c/4pcyVnov.
function getFundingEmail(currentUser: CurrentUser, cohort: Cohort, admissionOffer: AdmissionOffer): string | null {
    const suppressFundingEmail = !!admissionOffer.reportsTags?.includes('msba_launch_immediate_offer');
    const showFundingEmail =
        !!getProgramConfigValue<boolean>(cohort.programType, 'showFundingEmail') && !suppressFundingEmail;

    return showFundingEmail ? targetBrandConfig(currentUser).fundingEmail : null;
}

type UseSelectedPlanOptions = {
    selectablePaymentSituations: PlanSelectionOptions['selectablePaymentSituations'];
    additionalFinancingOptions: FinancingOption[];
    methods: ReturnType<typeof useForm<FormValues>>;
};

// Tracks which plan the learner has chosen.
function useSelectedPlan({ selectablePaymentSituations, additionalFinancingOptions, methods }: UseSelectedPlanOptions) {
    const selectedPaymentOptionId = useWatch({ control: methods.control, name: 'selectedPaymentOptionId' });
    const selectedPaymentSituation = selectablePaymentSituations.find(s => s.optionId === selectedPaymentOptionId);

    return {
        selectedPaymentOptionId,
        selectedFinancingOption: additionalFinancingOptions.find(o => o.optionId === selectedPaymentOptionId),
        selectedPlanAcceptsWireTransfer:
            !!selectedPaymentSituation && acceptsWireTransfer(selectedPaymentSituation.tuitionPlan),
    };
}

type PlanSelectionSmallPrintProps = {
    currentUser: CurrentUser;
    cohort: Cohort;
    cohortSectionOffer: CohortSectionOffer;
    showsRateAndApprovalNote: boolean;
    showsWireTransferNote: boolean;
    // Null unless the program shows a funding contact address on this view.
    fundingEmail: string | null;
    onViewWireTransferInstructions: () => void;
};

// The notes below the card. ExecEd registration has no deferral line.
function PlanSelectionSmallPrint({
    currentUser,
    cohort,
    cohortSectionOffer,
    showsRateAndApprovalNote,
    showsWireTransferNote,
    fundingEmail,
    onViewWireTransferInstructions,
}: PlanSelectionSmallPrintProps): ReactElement {
    const { t } = useTranslation('back_royal');

    return (
        <div className="flex flex-col gap-3">
            {!getIsExecEd(cohort) ? (
                <SmallPrint
                    currentUser={currentUser}
                    hasScholarship={isPartialScholarship(cohortSectionOffer.cumulativeOfferedScholarship)}
                />
            ) : null}

            {showsRateAndApprovalNote ? (
                <SmallPrintLine>{t(`${LOCALE_PREFIX}.rate_and_approval_based_on_credit`)}</SmallPrintLine>
            ) : null}

            {showsWireTransferNote ? (
                <SmallPrintLine>
                    <Trans
                        t={t}
                        i18nKey={`${LOCALE_PREFIX}.pay_by_wire_transfer`}
                        components={{
                            bankDetailsLink: (
                                <InlineLink className="text-2xs" onPress={onViewWireTransferInstructions} />
                            ),
                        }}
                    />
                </SmallPrintLine>
            ) : null}

            {fundingEmail ? (
                <SmallPrintLine>
                    <Trans
                        t={t}
                        i18nKey={`${LOCALE_PREFIX}.funding_options`}
                        components={{
                            mail: <MailtoLink className="text-2xs hover:text-primary" email={fundingEmail} />,
                        }}
                    />
                </SmallPrintLine>
            ) : null}
        </div>
    );
}

/*
    Customer.io outreach reads this event. Keep the payload shape, timestamp shifts included.
*/
function logFinancingOptionSelected(
    injector: ReturnType<typeof useAngularContext>,
    {
        admissionOffer,
        cohort,
        cohortSectionOffer,
        registerForCohortId,
        option,
    }: {
        admissionOffer: AdmissionOffer;
        cohort: Cohort;
        cohortSectionOffer: CohortSectionOffer;
        registerForCohortId: string;
        option: FinancingOption;
    },
) {
    const dateHelper = injector?.get<{ shiftMonthDayForThreshold: (d: Date) => Date }>('dateHelper');
    if (!dateHelper) return;

    injector?.get<{ log: (name: string, payload: object, opts: object) => void }>('EventLogger').log(
        'cohort_registration:financing_option_selected',
        {
            admission_offer_id: admissionOffer.id,
            cohort_id: registerForCohortId,
            program_type: cohort.programType,
            cohort_registration_deadline:
                dateHelper.shiftMonthDayForThreshold(getCohortRegistrationDeadline(cohort)).getTime() / 1000,
            early_registration_deadline: admissionOffer.earlyRegistrationDeadline
                ? dateHelper
                      .shiftMonthDayForThreshold(new Date(admissionOffer.earlyRegistrationDeadline * 1000))
                      .getTime() / 1000
                : null,
            net_required_payment: cohortSectionOffer.basePaymentSituation.paymentInformation.netRequiredPayment,
            selected_financing_option: {
                label: option.additionalFinancingOption.label,
                monthly_duration: option.additionalFinancingOption.monthlyDuration,
                apr: Math.round(option.additionalFinancingOption.apr * 10000) / 100,
                payment_amount_per_interval: option.paymentInformation.formattedPaymentAmountPerInterval,
            },
        },
        { log_to_customerio: true },
    );
}
